import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "antd";
import moment from "moment";
import {
  MONTH_OPTIONS,
  getFinanceYearOptions,
  getMonthLabel,
} from "../../../app/utils/finance/salaryCalculations";

const toDateStr = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const previousPeriod = (year, month) => {
  if (Number(month) === 1) return { year: Number(year) - 1, month: 12 };
  return { year: Number(year), month: Number(month) - 1 };
};

const RemoveSalaryStructureModal = ({
  isOpen,
  structure,
  lastWorkingDate,
  onClose,
  onConfirm,
  confirmLoading = false,
}) => {
  const existingLastWorkingDate = toDateStr(lastWorkingDate || structure?.lastWorkingDate);
  const yearOptions = useMemo(() => getFinanceYearOptions(), []);
  const current = useMemo(() => {
    const now = moment();
    return { year: now.year(), month: now.month() + 1 };
  }, []);

  const [lastWorkingYear, setLastWorkingYear] = useState(current.year);
  const [lastWorkingMonth, setLastWorkingMonth] = useState(current.month);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setError("");
      return;
    }
    if (existingLastWorkingDate) {
      const parsed = moment(existingLastWorkingDate, "YYYY-MM-DD", true);
      setLastWorkingYear(parsed.isValid() ? parsed.year() : current.year);
      setLastWorkingMonth(parsed.isValid() ? parsed.month() + 1 : current.month);
      return;
    }
    const prev = previousPeriod(current.year, current.month);
    setLastWorkingYear(prev.year);
    setLastWorkingMonth(prev.month);
  }, [isOpen, existingLastWorkingDate, current.year, current.month, structure?.id]);

  const employeeName =
    `${structure?.firstname || ""} ${structure?.lastname || ""}`.trim() || "this employee";

  const handleConfirm = () => {
    if (!lastWorkingYear || !lastWorkingMonth) {
      setError("Select the last working month.");
      return;
    }
    const selectedIndex = Number(lastWorkingYear) * 12 + Number(lastWorkingMonth);
    const currentIndex = current.year * 12 + current.month;
    if (selectedIndex > currentIndex) {
      setError("Last working month cannot be in the future.");
      return;
    }
    onConfirm({
      lastWorkingYear: Number(lastWorkingYear),
      lastWorkingMonth: Number(lastWorkingMonth),
      lastWorkingDate: existingLastWorkingDate || null,
    });
  };

  const nextMonthLabel = useMemo(() => {
    const next =
      Number(lastWorkingMonth) === 12
        ? { year: Number(lastWorkingYear) + 1, month: 1 }
        : { year: Number(lastWorkingYear), month: Number(lastWorkingMonth) + 1 };
    return `${getMonthLabel(next.month)} ${next.year}`;
  }, [lastWorkingYear, lastWorkingMonth]);

  return (
    <Modal
      title="Remove Employee from Salary Structure"
      open={isOpen}
      zIndex={1200}
      onCancel={confirmLoading ? undefined : onClose}
      closable={!confirmLoading}
      maskClosable={!confirmLoading}
      keyboard={!confirmLoading}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={confirmLoading}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          danger
          onClick={handleConfirm}
          loading={confirmLoading}
          disabled={confirmLoading}
        >
          Remove
        </Button>,
      ]}
    >
      <p className="text-sm text-[#27314a] mb-4">
        Remove <strong>{employeeName}</strong> from salary structure. They will stay on the last
        working month sheet and be removed from later months.
      </p>

      {existingLastWorkingDate ? (
        <p className="text-sm text-[#27314a] mb-3">
          Last working day on record:{" "}
          <strong>{moment(existingLastWorkingDate).format("DD MMM YYYY")}</strong>
        </p>
      ) : (
        <p className="text-sm text-[#6f7d97] mb-3">
          No last working day is saved. Select the last working month.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 mb-2">
        <label className="text-xs font-medium text-[#6f7d97]">
          Last working month
          <select
            className="mt-1 w-full border border-[#e6ebf6] rounded-lg px-3 py-2 text-sm text-[#27314a]"
            value={lastWorkingMonth}
            onChange={(e) => {
              setLastWorkingMonth(Number(e.target.value));
              setError("");
            }}
            disabled={confirmLoading || Boolean(existingLastWorkingDate)}
          >
            {MONTH_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-medium text-[#6f7d97]">
          Year
          <select
            className="mt-1 w-full border border-[#e6ebf6] rounded-lg px-3 py-2 text-sm text-[#27314a]"
            value={lastWorkingYear}
            onChange={(e) => {
              setLastWorkingYear(Number(e.target.value));
              setError("");
            }}
            disabled={confirmLoading || Boolean(existingLastWorkingDate)}
          >
            {yearOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? <p className="text-xs text-red-600 mt-1">{error}</p> : null}
      <p className="text-xs text-[#6f7d97] mt-2">
        {getMonthLabel(lastWorkingMonth)} {lastWorkingYear} salary stays. They are removed from{" "}
        {nextMonthLabel} onward (unpaid rows only).
      </p>
    </Modal>
  );
};

export default RemoveSalaryStructureModal;
